import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { MainLayout } from '../layouts';
import { useAuth } from '../contexts/AuthContext';
import {
  GET_ALL_USER_SUBSCRIPTIONS,
  CANCEL_SUBSCRIPTION,
  GET_SUBSCRIPTION_PACKAGES,
  CHANGE_SUBSCRIPTION_PLAN,
  CANCEL_PENDING_PLAN_CHANGE,
} from '@shared/graphql/queries/subscriptionPackages';
import InvoiceAccess from '../components/subscription/InvoiceAccess';
import BillingDetailsSection from '../components/subscription/BillingDetailsSection';
import ChangePlanModal, { AvailablePackage, BillingCycleValue } from '../components/subscription/ChangePlanModal';
import analyticsService from '../services/analytics';

interface AppliedDiscount {
  id: string;
  discountCode: string;
  discountName?: string;
  discountDescription?: string;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  paymentReference: string;
  paymentType: string;
  appliedAt: string;
  stripePromotionCodeId?: string;
  stripeCouponId?: string;
}

interface UserSubscription {
  id: string;
  userId: string;
  package: {
    id: string;
    packageCode: string;
    name: string;
    description: string;
    priceWeekly: number;
    priceMonthly: number;
    priceOneTime?: number;
    billingModel: string;
    accessDurationDays?: number;
    allowMultipleSubscriptions: boolean;
    displayOrder: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
  billingCycle: string;
  subscriptionOrder: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  autoRenew: boolean;
  createdAt: string;
  updatedAt: string;
  currentPrice: number;
  isDefaultPackage?: boolean;
  selectedTracks?: Array<{
    id: string;
    userSubscriptionId: string;
    trackId: string;
    trackName?: string;
    trackState?: string;
    trackType?: string;
    createdAt: string;
  }>;
  appliedDiscount?: AppliedDiscount;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
  canceledAt?: string;
  pendingPackageCode?: string;
  pendingBillingCycle?: string;
  pendingEffectiveDate?: string;
}

interface SubscriptionData {
  getCurrentUserSubscription: {
    activeSubscriptions: UserSubscription[];
    expiredSubscriptions: UserSubscription[];
    totalWeeklyCost: number;
    totalMonthlyCost: number;
    currentBillingCycle: string;
    accessibleTracks: string[];
  };
}

// Admin/promotional/complimentary packages can't be self-changed (mirrors the
// cancel-button gating).
const isAdminLikePackage = (sub: UserSubscription): boolean => {
  const code = sub.package.packageCode;
  const name = (sub.package.name || '').toLowerCase();
  return code === 'FREE_FOR_LIFE' || code === 'FREE_TRACKS' ||
    name.includes('friends') || name.includes('free') ||
    name.includes('admin') || name.includes('promotional');
};

// A subscription a user may switch to another plan: an active, auto-renewing,
// recurring (non one-time) subscription that isn't cancelling or admin-granted.
const isPlanChangeEligible = (sub: UserSubscription): boolean =>
  !!sub.isActive && !!sub.autoRenew && !sub.cancelAtPeriodEnd &&
  sub.package.billingModel?.toLowerCase() !== 'one_time' &&
  !sub.isDefaultPackage && !isAdminLikePackage(sub);

const ProfileSubscriptionsPage: React.FC = () => {
  const { user } = useAuth();
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isExpiredExpanded, setIsExpiredExpanded] = useState(false);
  const [isInvoiceExpanded, setIsInvoiceExpanded] = useState(false);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<UserSubscription | null>(null);
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [changePlanSubscription, setChangePlanSubscription] = useState<UserSubscription | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [changePlanError, setChangePlanError] = useState('');
  const [cancellingPendingChange, setCancellingPendingChange] = useState(false);

  const { data, loading, error, refetch } = useQuery<SubscriptionData>(GET_ALL_USER_SUBSCRIPTIONS, {
    skip: !user,
    errorPolicy: 'all'
  });

  const { data: packagesData } = useQuery(GET_SUBSCRIPTION_PACKAGES, {
    skip: !user,
    errorPolicy: 'all'
  });
  const availablePackages: AvailablePackage[] = packagesData?.getAvailablePackages || [];

  // Analytics page view tracking
  useEffect(() => {
    try {
      analyticsService.trackPageView('/profile/subscriptions', 'Profile Subscriptions Page');
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }, []);

  const [cancelSubscriptionMutation] = useMutation(CANCEL_SUBSCRIPTION, {
    onCompleted: () => {
      setCancelSuccess(true);
      setCancelMessage('Membership cancelled successfully. You will retain access until the end of your current billing period.');
      setShowCancelModal(false);
      setCancellingSubscriptionId(null);
      
      // Track subscription cancellation
      try {
        analyticsService.trackEvent('subscription_cancelled', {
          metadata: {
            subscriptionId: selectedSubscription?.id,
            packageCode: selectedSubscription?.package.packageCode,
            packageName: selectedSubscription?.package.name,
            billingCycle: selectedSubscription?.billingCycle,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      // Add debugging before refetch
      console.log('🔍 FRONTEND_DEBUG: Cancellation completed, about to refetch data');
      refetch().then((result) => {
        console.log('🔍 FRONTEND_DEBUG: Refetch completed, new data:', result.data?.getCurrentUserSubscription?.activeSubscriptions);
        
        // Log the specific subscription that was cancelled
        const cancelledSub = result.data?.getCurrentUserSubscription?.activeSubscriptions?.find(
          (sub: any) => sub.id === selectedSubscription?.id
        );
        if (cancelledSub) {
          console.log('🔍 FRONTEND_DEBUG: Cancelled subscription data after refetch:', {
            id: cancelledSub.id,
            packageCode: cancelledSub.package.packageCode,
            autoRenew: cancelledSub.autoRenew,
            cancelAtPeriodEnd: cancelledSub.cancelAtPeriodEnd,
            isActive: cancelledSub.isActive
          });
        } else {
          console.log('🔍 FRONTEND_DEBUG: Could not find cancelled subscription in refetched data');
        }
      }); // Refresh the subscription data
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setCancelMessage('');
        setCancelSuccess(false);
      }, 3000);
    },
    onError: (error) => {
      setCancelSuccess(false);
      setCancelMessage(error.message || 'Failed to cancel membership. Please try again.');
      setCancellingSubscriptionId(null);
    }
  });

  const [changeSubscriptionPlanMutation] = useMutation(CHANGE_SUBSCRIPTION_PLAN);
  const [cancelPendingPlanChangeMutation] = useMutation(CANCEL_PENDING_PLAN_CHANGE);

  const handleCancelSubscription = (subscription: UserSubscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
  };

  const handleChangePlan = (subscription: UserSubscription) => {
    setChangePlanSubscription(subscription);
    setChangePlanError('');
    setShowChangePlanModal(true);
  };

  const confirmChangePlan = async (newPackageId: number, newBillingCycle: BillingCycleValue) => {
    try {
      setChangingPlan(true);
      setChangePlanError('');
      await changeSubscriptionPlanMutation({ variables: { newPackageId, newBillingCycle } });

      try {
        analyticsService.trackEvent('subscription_plan_change', {
          metadata: {
            fromPackageCode: changePlanSubscription?.package.packageCode,
            toPackageId: newPackageId,
            toBillingCycle: newBillingCycle,
            timestamp: new Date().toISOString()
          }
        });
      } catch (analyticsError) {
        console.error('Analytics tracking error:', analyticsError);
      }

      await refetch();
      setShowChangePlanModal(false);
      setChangePlanSubscription(null);
      setCancelSuccess(true);
      setCancelMessage('Plan change scheduled. It will take effect at the end of your current billing period.');
      setTimeout(() => {
        setCancelMessage('');
        setCancelSuccess(false);
      }, 4000);
    } catch (err: any) {
      setChangePlanError(err.message || 'Failed to change plan. Please try again.');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleCancelPendingChange = async () => {
    try {
      setCancellingPendingChange(true);
      await cancelPendingPlanChangeMutation();

      try {
        analyticsService.trackEvent('subscription_plan_change_cancelled', {
          metadata: { timestamp: new Date().toISOString() }
        });
      } catch (analyticsError) {
        console.error('Analytics tracking error:', analyticsError);
      }

      await refetch();
      setCancelSuccess(true);
      setCancelMessage('Pending plan change cancelled. Your current plan will continue unchanged.');
      setTimeout(() => {
        setCancelMessage('');
        setCancelSuccess(false);
      }, 4000);
    } catch (err: any) {
      setCancelSuccess(false);
      setCancelMessage(err.message || 'Failed to cancel pending change. Please try again.');
    } finally {
      setCancellingPendingChange(false);
    }
  };

  const confirmCancelSubscription = async () => {
    if (!selectedSubscription) return;

    try {
      setCancellingSubscriptionId(selectedSubscription.id);
      setCancelMessage('');
      
      await cancelSubscriptionMutation({
        variables: {
          subscriptionId: parseInt(selectedSubscription.id)
        }
      });
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    }
  };

  const formatDate = (dateString: string) => {
    // Convert UTC date to Australian timezone (AEST/AEDT)
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Australia/Brisbane' // Use Brisbane timezone (AEST/AEDT)
    });
  };

  const formatDateTime = (dateString: string) => {
    // Convert UTC datetime to Australian timezone with time
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Australia/Brisbane',
      hour12: true
    });
  };

  const formatEndDate = (dateString: string) => {
    // Format end date without time for user-friendly display
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Australia/Brisbane'
    });
  };

  const formatLastAccessDate = (nextBillingDateString: string) => {
    // Calculate the last day of access (one day before the next billing date)
    const nextBillingDate = new Date(nextBillingDateString);
    const lastAccessDate = new Date(nextBillingDate);
    lastAccessDate.setDate(lastAccessDate.getDate() - 1);
    
    return lastAccessDate.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Australia/Brisbane'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const getBillingCycleText = (billingCycle: string) => {
    return billingCycle.toLowerCase() === 'monthly' ? 'Monthly' : 'Weekly';
  };

  const SubscriptionCard: React.FC<{ 
    subscription: UserSubscription; 
    isExpired?: boolean; 
    showCancelButton?: boolean 
  }> = ({ subscription, isExpired = false, showCancelButton = true }) => {
   // Enhanced cancel button logic with safety checks
   // Hide cancel button for admin packages (private packages that are admin-created)
   const isAdminPackage = subscription.package.packageCode === 'FREE_FOR_LIFE' ||
                         subscription.package.packageCode === 'FREE_TRACKS' ||
                         subscription.package.name.toLowerCase().includes('friends') ||
                         subscription.package.name.toLowerCase().includes('free') ||
                         subscription.package.name.toLowerCase().includes('admin') ||
                         subscription.package.name.toLowerCase().includes('promotional');
   
   const shouldShowCancelButton = !isExpired &&
     showCancelButton &&
     !subscription.cancelAtPeriodEnd &&
     subscription.autoRenew &&
     subscription.isActive &&
     subscription.package.billingModel !== 'ONE_TIME' &&
     !subscription.isDefaultPackage &&
     !isAdminPackage;

   // Plan change is v1-limited to users with exactly one eligible recurring sub.
   const eligibleRecurringCount = activeSubscriptions.filter(isPlanChangeEligible).length;
   const hasPendingChange = !isExpired && !!subscription.pendingEffectiveDate;
   const shouldShowChangeButton = !isExpired &&
     isPlanChangeEligible(subscription) &&
     eligibleRecurringCount === 1 &&
     !hasPendingChange;

   console.log('🔍 FRONTEND_DEBUG: SubscriptionCard cancel button logic for', subscription.package.packageCode, {
     isExpired,
     showCancelButton,
     cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
     billingModel: subscription.package.billingModel,
     autoRenew: subscription.autoRenew,
     isActive: subscription.isActive,
     shouldShowCancelButton
   });
    
    const isOneTimePackage = subscription.package.billingModel?.toLowerCase() === 'one_time';
    const isOneTimeActive = isOneTimePackage && !isExpired;
    const isOneTimeExpired = isOneTimePackage && isExpired;
    
    return (
    <div className={`p-3 sm:p-4 rounded-lg border ${
      isExpired ? 'bg-gray-50 border-gray-200' :
      subscription.isDefaultPackage ? 'bg-green-50 border-green-300' :
      'bg-white border-racing-green'
    }`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <h3 className={`text-base sm:text-lg font-semibold ${
              isExpired ? 'text-gray-600' :
              subscription.isDefaultPackage ? 'text-green-700' :
              'text-racing-green'
            }`}>
              {subscription.package.name}
            </h3>
            <div className="flex flex-wrap gap-2">
              {subscription.isDefaultPackage && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  Complimentary Access
                </span>
              )}
              {isOneTimePackage && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                  One-Time Purchase
                </span>
              )}
            </div>
          </div>
          {subscription.isDefaultPackage ? (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">
                    Complimentary Promotional Access
                  </h4>
                  <p className="text-sm text-blue-700">
                    {subscription.package.description}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">{subscription.package.description}</p>
          )}
        </div>
        <div className="flex sm:justify-end">
          <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isExpired
                ? 'bg-red-100 text-red-800'
                : (subscription.cancelAtPeriodEnd && !isOneTimePackage)
                  ? 'bg-amber-100 text-amber-800'
                  : subscription.isDefaultPackage
                    ? 'bg-green-100 text-green-800'
                    : 'bg-green-100 text-green-800'
            }`}>
              {isExpired
                ? 'Expired'
                : (subscription.cancelAtPeriodEnd && !isOneTimePackage)
                  ? 'Cancelled'
                  : 'Active'
              }
            </span>
            {subscription.isDefaultPackage && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Promotional
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Billing Cycle:</span> {
              subscription.isDefaultPackage ? 'Complimentary' :
              isOneTimePackage ? 'One-Time Purchase' :
              getBillingCycleText(subscription.billingCycle)
            }
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Current Price:</span> {
              subscription.isDefaultPackage ? 'Free' :
              isOneTimePackage && subscription.package.priceOneTime ?
              formatCurrency(subscription.package.priceOneTime) :
              formatCurrency(subscription.currentPrice)
            }
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Start Date:</span> {formatDate(subscription.startDate)}
          </p>
          {/* Show billing information based on subscription status and type */}
          {isOneTimePackage && subscription.endDate ? (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Last day of access:</span> {formatEndDate(subscription.endDate)}
            </p>
          ) : !isOneTimePackage && (subscription.currentPeriodEnd || subscription.endDate) ? (
            <>
              {subscription.cancelAtPeriodEnd ? (
                // Show last day of access for cancelled subscriptions
                <p className="text-sm text-amber-600">
                  <span className="font-medium">Last day of access:</span> {formatLastAccessDate(subscription.currentPeriodEnd || subscription.endDate || '')}
                </p>
              ) : (
                // Show next billing date for active subscriptions
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Next billing date:</span> {formatEndDate(subscription.currentPeriodEnd || subscription.endDate || '')}
                </p>
              )}
            </>
          ) : null}
        </div>
        
        <div className="space-y-2">
          {subscription.selectedTracks && subscription.selectedTracks.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Selected Tracks:</p>
              <div className="text-sm text-gray-600">
                {subscription.selectedTracks.map((track, index) => (
                  <span key={track.id} className="inline-block">
                    {track.trackName} ({track.trackState})
                    {index < subscription.selectedTracks!.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-600">
            <span className="font-medium">Package Code:</span>
            <span className="block sm:inline sm:ml-1">{subscription.package.packageCode}</span>
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Auto Renew:</span>
            <span className="block sm:inline sm:ml-1">{
              subscription.isDefaultPackage ? 'N/A (Promotional)' :
              isOneTimePackage ? 'No' :
              subscription.cancelAtPeriodEnd ? 'No (Cancelled)' :
              (subscription.autoRenew ? 'Yes' : 'No')
            }</span>
          </p>
        </div>
      </div>

      {/* Applied Discount Information */}
      {subscription.appliedDiscount && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                </svg>
                <h4 className="text-sm font-semibold text-green-800">
                  Discount Applied: {subscription.appliedDiscount.discountCode}
                </h4>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-green-700 font-medium">Original Price:</span>
                  <div className="text-green-800">{formatCurrency(subscription.appliedDiscount.originalAmount)}</div>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Discount:</span>
                  <div className="text-green-800">-{formatCurrency(subscription.appliedDiscount.discountAmount)}</div>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Final Price:</span>
                  <div className="text-green-800 font-semibold">{formatCurrency(subscription.appliedDiscount.finalAmount)}</div>
                </div>
              </div>

              {(subscription.appliedDiscount.discountName || subscription.appliedDiscount.discountDescription) && (
                <div className="mt-2 text-sm text-green-700">
                  {subscription.appliedDiscount.discountName && (
                    <div className="font-medium">{subscription.appliedDiscount.discountName}</div>
                  )}
                  {subscription.appliedDiscount.discountDescription && (
                    <div className="text-green-600">{subscription.appliedDiscount.discountDescription}</div>
                  )}
                </div>
              )}

              <div className="mt-2 text-xs text-green-600">
                Applied: {formatDateTime(subscription.appliedDiscount.appliedAt)}
              </div>
            </div>

            <div className="ml-3 text-right">
              <div className="text-lg font-bold text-green-800">
                {Math.round((subscription.appliedDiscount.discountAmount / subscription.appliedDiscount.originalAmount) * 100)}% OFF
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending plan change banner */}
      {hasPendingChange && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Plan change scheduled:</span> switching to{' '}
              <strong>{subscription.pendingPackageCode}</strong>
              {subscription.pendingBillingCycle ? ` (${getBillingCycleText(subscription.pendingBillingCycle)})` : ''}{' '}
              on <strong>{formatEndDate(subscription.pendingEffectiveDate || '')}</strong>.
            </p>
            <button
              onClick={handleCancelPendingChange}
              disabled={cancellingPendingChange}
              className={`text-blue-700 underline hover:text-blue-900 transition text-xs sm:text-sm whitespace-nowrap ${
                cancellingPendingChange ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {cancellingPendingChange ? 'Cancelling…' : 'Cancel change'}
            </button>
          </div>
        </div>
      )}

      {(shouldShowCancelButton || shouldShowChangeButton) && (
        <div className="flex justify-end gap-2">
          {shouldShowChangeButton && (
            <button
              onClick={() => handleChangePlan(subscription)}
              className="bg-racing-green text-white py-2 px-3 sm:px-4 rounded hover:bg-racing-green-dark transition flex items-center text-xs sm:text-sm"
            >
              Change Plan
            </button>
          )}
          {shouldShowCancelButton && (
            <button
              onClick={() => handleCancelSubscription(subscription)}
              disabled={cancellingSubscriptionId === subscription.id}
              className={`bg-red-600 text-white py-2 px-3 sm:px-4 rounded hover:bg-red-700 transition flex items-center text-xs sm:text-sm ${
                cancellingSubscriptionId === subscription.id ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {cancellingSubscriptionId === subscription.id ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cancelling...
                </>
              ) : (
                'Cancel Membership'
              )}
            </button>
          )}
        </div>
      )}
      
      
      {/* One-Time Package Information */}
      {isOneTimePackage && !subscription.isDefaultPackage && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                One-Time Purchase
              </h4>
              <p className="text-sm text-yellow-700">
                {isOneTimeActive
                  ? `This is a one-time purchase that provides access until ${subscription.endDate ? formatDate(subscription.endDate) : 'the end of the access period'}.`
                  : 'This was a one-time purchase that has now expired.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
          <div className="text-center text-gray-500">
            <svg className="animate-spin h-8 w-8 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p>Loading your subscriptions...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
          <div className="text-center text-red-600">
            <p className="mb-4">Error loading subscriptions: {error.message}</p>
            <button
              onClick={() => refetch()}
              className="bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const subscriptionData = data?.getCurrentUserSubscription;
  const activeSubscriptions = subscriptionData?.activeSubscriptions || [];
  const expiredSubscriptions = subscriptionData?.expiredSubscriptions || [];
  
  // Add debugging to see what data is being rendered
  console.log('🔍 FRONTEND_DEBUG: Rendering subscriptions page with data:', {
    activeSubscriptionsCount: activeSubscriptions.length,
    activeSubscriptions: activeSubscriptions.map((sub: any) => ({
      id: sub.id,
      packageCode: sub.package.packageCode,
      autoRenew: sub.autoRenew,
      cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
      isActive: sub.isActive
    }))
  });

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Membership Management</h1>
          <Link
            to="/profile"
            className="text-racing-green hover:text-racing-green-dark transition text-sm sm:text-base"
          >
            ← Back to Profile
          </Link>
        </div>

        {cancelMessage && (
          <div className={`p-3 rounded mb-6 ${cancelSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {cancelSuccess && (
              <svg className="w-5 h-5 inline-block mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {cancelMessage}
          </div>
        )}

        {/* Active Subscriptions Section */}
        <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
          <button
            onClick={() => {
              setIsActiveExpanded(!isActiveExpanded);
              // Track section expansion
              try {
                analyticsService.trackEvent('subscription_section_toggled', {
                  metadata: {
                    section: 'active_subscriptions',
                    expanded: !isActiveExpanded,
                    timestamp: new Date().toISOString()
                  }
                });
              } catch (error) {
                console.error('Analytics tracking error:', error);
              }
            }}
            className="w-full flex justify-between items-center text-left"
          >
            <div>
              <h2 className="text-lg font-semibold">Active Memberships</h2>
              <p className="text-sm text-gray-600">
              {activeSubscriptions.length} active membership{activeSubscriptions.length !== 1 ? 's' : ''}
                {activeSubscriptions.some(sub => sub.isDefaultPackage) && (
                  <span className="ml-1 text-green-600 font-medium">
                    (includes promotional access)
                  </span>
                )}
              </p>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${isActiveExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isActiveExpanded && (
            <div className="mt-4">
              {activeSubscriptions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  You don't have any active memberships.
                  <br />
                  <Link
                    to="/subscriptions"
                    className="text-racing-green hover:text-racing-green-dark transition mt-2 inline-block"
                  >
                    Browse available packages →
                  </Link>
                </p>
              ) : (
                <div className="space-y-4">
                  {activeSubscriptions.map((subscription) => (
                    <SubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      isExpired={false}
                      showCancelButton={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expired Subscriptions Section */}
        <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
          <button
            onClick={() => {
              setIsExpiredExpanded(!isExpiredExpanded);
              // Track section expansion
              try {
                analyticsService.trackEvent('subscription_section_toggled', {
                  metadata: {
                    section: 'expired_subscriptions',
                    expanded: !isExpiredExpanded,
                    timestamp: new Date().toISOString()
                  }
                });
              } catch (error) {
                console.error('Analytics tracking error:', error);
              }
            }}
            className="w-full flex justify-between items-center text-left"
          >
            <div>
              <h2 className="text-lg font-semibold">Membership History</h2>
              <p className="text-sm text-gray-600">
              {expiredSubscriptions.length} expired membership{expiredSubscriptions.length !== 1 ? 's' : ''}
              </p>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${isExpiredExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isExpiredExpanded && (
            <div className="mt-4">
              {expiredSubscriptions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No membership history found.
                </p>
              ) : (
                <div className="space-y-4">
                  {expiredSubscriptions.map((subscription) => (
                    <SubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      isExpired={true}
                      showCancelButton={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Invoice Access Section */}
        <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
          <button
            onClick={() => {
              setIsInvoiceExpanded(!isInvoiceExpanded);
              // Track section expansion
              try {
                analyticsService.trackEvent('subscription_section_toggled', {
                  metadata: {
                    section: 'invoices',
                    expanded: !isInvoiceExpanded,
                    timestamp: new Date().toISOString()
                  }
                });
              } catch (error) {
                console.error('Analytics tracking error:', error);
              }
            }}
            className="w-full flex justify-between items-center text-left"
          >
            <div>
              <h2 className="text-lg font-semibold">Invoices</h2>
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${isInvoiceExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isInvoiceExpanded && (
            <div className="mt-4">
              <InvoiceAccess />
            </div>
          )}
        </div>

        {/* Billing Details Section */}
        <BillingDetailsSection />

        {/* Cancellation Confirmation Modal */}
        {showCancelModal && selectedSubscription && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
              <h3 className="text-base sm:text-lg font-semibold text-red-800 mb-4">Cancel Membership</h3>
              <p className="text-sm text-red-700 mb-4">
                ⚠️ <strong>Warning:</strong> Are you sure you want to cancel your membership to <strong>{selectedSubscription.package.name}</strong>?
              </p>
              <p className="text-sm text-gray-600 mb-6">
                You will continue to have access until your current billing period ends
                {selectedSubscription.currentPeriodEnd ? (
                  <span>. Your last day of access will be <strong>{formatLastAccessDate(selectedSubscription.currentPeriodEnd)}</strong></span>
                ) : selectedSubscription.endDate ? (
                  <span>. Your last day of access will be <strong>{formatLastAccessDate(selectedSubscription.endDate)}</strong></span>
                ) : (
                  <span className="text-red-600">. Unable to determine last access date</span>
                )}.
                The membership will not auto-renew after cancellation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={confirmCancelSubscription}
                  disabled={cancellingSubscriptionId === selectedSubscription.id}
                  className={`bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition flex items-center justify-center text-sm ${
                    cancellingSubscriptionId === selectedSubscription.id ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {cancellingSubscriptionId === selectedSubscription.id ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Cancelling...
                    </>
                  ) : (
                    'Confirm - Cancel Membership'
                  )}
                </button>
                
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedSubscription(null);
                    setCancelMessage('');
                  }}
                  className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition text-sm"
                >
                  Keep Membership
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Plan Modal */}
        {showChangePlanModal && changePlanSubscription && (
          <ChangePlanModal
            currentPackageCode={changePlanSubscription.package.packageCode}
            currentBillingCycle={changePlanSubscription.billingCycle}
            availablePackages={availablePackages}
            effectiveDateText={formatEndDate(changePlanSubscription.currentPeriodEnd || changePlanSubscription.endDate || '')}
            loading={changingPlan}
            errorMessage={changePlanError}
            formatCurrency={formatCurrency}
            onConfirm={confirmChangePlan}
            onClose={() => {
              setShowChangePlanModal(false);
              setChangePlanSubscription(null);
              setChangePlanError('');
            }}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default ProfileSubscriptionsPage;